import React from 'react';

// Text formatting utilities
export const formatText = (text) => {
  if (!text) return '';
  
  // Enhanced WordPress formatting support - convert markdown to HTML
  let formatted = text
    // Convert markdown bold (**text**) to HTML
    .replace(/\*\*([^*\n]+)\*\*/g, '<strong class="font-semibold">$1</strong>')
    // Convert markdown italic (*text*) to HTML  
    .replace(/\*([^*\n]+)\*/g, '<em class="italic">$1</em>')
    // Convert markdown headers (## text) to HTML with proper styling
    .replace(/## ([^\n]+)/g, '<h3 class="text-xl font-bold text-gray-900 mb-3 mt-4">$1</h3>')
    .replace(/### ([^\n]+)/g, '<h4 class="text-lg font-semibold text-gray-800 mb-2 mt-3">$1</h4>')
    // Convert URLs to clickable links (new tab, secure)
    .replace(/(https?:\/\/[^\s<>"'()]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline font-medium">$1 ↗</a>')
    // Double line breaks create proper paragraph spacing
    .replace(/\n\n+/g, '</p><p class="mb-4 leading-relaxed">')
    // Single line breaks become <br>
    .replace(/\n/g, '<br>');
  
  // Wrap in paragraph tags with proper spacing and typography
  formatted = `<p class="mb-4 leading-relaxed text-gray-700">${formatted}</p>`;
  
  return formatted;
};

export const getFirstSentences = (text, sentenceCount = 4) => {
  if (!text) return '';
  
  // Split by sentence endings, keeping the punctuation
  const sentences = text.match(/[^\.!?]+[\.!?]+/g) || [text];
  
  if (sentences.length <= sentenceCount) {
    return text;
  }
  
  return sentences.slice(0, sentenceCount).join(' ').trim();
};

export const stripFormatting = (text) => {
  if (!text) return '';
  // No formatting to strip anymore, just return the text
  return text;
};

// Component for displaying formatted text content
export const FormattedContent = ({ 
  content, 
  maxSentences = null,
  maxLines = null,
  className = '',
  showReadMore = false,
  onToggleExpand = null,
  isExpanded = false 
}) => {
  if (!content) return null;
  
  let displayContent = content;
  let needsExpansion = false;
  
  if (maxSentences && !isExpanded) {
    const truncated = getFirstSentences(content, maxSentences);
    if (truncated !== content) {
      displayContent = truncated;
      needsExpansion = true;
    }
  }
  
  const formattedContent = formatText(displayContent);
  
  // Calculate styles for line limiting
  const lineHeightValue = 1.6;
  const maxHeight = maxLines ? `${maxLines * lineHeightValue}em` : 'none';
  const isOverflowing = maxLines && content.split('\n').length > maxLines;
  
  return (
    <div className={className}>
      <div 
        className="prose prose-sm max-w-none"
        dangerouslySetInnerHTML={{ __html: formattedContent }}
        style={{
          lineHeight: lineHeightValue,
          fontSize: 'inherit',
          maxHeight: maxHeight,
          overflow: maxLines ? 'hidden' : 'visible',
          display: maxLines ? '-webkit-box' : 'block',
          WebkitLineClamp: maxLines || 'none',
          WebkitBoxOrient: 'vertical',
          textOverflow: 'ellipsis'
        }}
      />
      
      {showReadMore && needsExpansion && (
        <button
          onClick={onToggleExpand}
          className="mt-2 text-blue-600 hover:text-blue-800 font-medium text-sm flex items-center space-x-1"
        >
          <span>Read more</span>
          <span>→</span>
        </button>
      )}
      
      {showReadMore && isExpanded && maxSentences && (
        <button
          onClick={onToggleExpand}
          className="mt-2 text-gray-600 hover:text-gray-800 font-medium text-sm flex items-center space-x-1"
        >
          <span>Show less</span>
          <span>↑</span>
        </button>
      )}
    </div>
  );
};